import { useMemo } from "react";
import type {
  AdultGigRequestListItemResponse,
  AdultGigRequestTeenDetailResponse,
  GigRequestStatus,
} from "../../../types/gigTypes";
import TeenRequestRow from "./TeenRequestRow";

type RequestGroup = {
  key: string;
  title: string;
  description: string;
  statuses: GigRequestStatus[];
};

const REQUEST_GROUPS: RequestGroup[] = [
  {
    key: "selected",
    title: "Selected",
    description: "Applicants selected before payment or final confirmation.",
    statuses: ["POSTER_SELECTED_PENDING_CREW", "PAYOUT_SETUP_PENDING", "FUNDING_PENDING"],
  },
  {
    key: "pending",
    title: "Pending",
    description: "Applicants still waiting on guardian approval or your decision.",
    statuses: ["POSTER_PENDING", "GUARDIAN_PENDING"],
  },
  {
    key: "confirmed",
    title: "Confirmed",
    description: "Applicants already accepted for this gig.",
    statuses: ["POSTER_ACCEPTED"],
  },
  {
    key: "no-longer-needed",
    title: "No Longer Needed",
    description: "Applicants closed because the gig spots were already filled.",
    statuses: ["FILLED_BY_OTHER_TEEN"],
  },
  {
    key: "rejected",
    title: "Rejected",
    description: "Applicants declined by a guardian or the poster.",
    statuses: ["POSTER_REJECTED", "GUARDIAN_REJECTED"],
  },
  {
    key: "withdrawn",
    title: "Withdrawn",
    description: "Applicants who withdrew their request.",
    statuses: ["WITHDRAWN"],
  },
];

type TeenRequestsPanelProps = {
  gigId: string;
  requiresFunding: boolean;
  selectsBeforePayment?: boolean;
  requests: AdultGigRequestListItemResponse[];
  teenDetailsByRequestId?: Record<string, AdultGigRequestTeenDetailResponse>;
  loadingTeenRequestIds?: string[];
  highlightedRequestId?: string | null;
  approvingRequestId: string | null;
  rejectingRequestId: string | null;
  schedulingRequestId: string | null;
  onAcceptTeen: (gigId: string, request: AdultGigRequestListItemResponse) => Promise<void>;
  onRejectTeen: (gigId: string, request: AdultGigRequestListItemResponse) => void;
  onViewTeen: (gigId: string, request: AdultGigRequestListItemResponse) => void;
  onOpenConversation: (request: AdultGigRequestListItemResponse) => void;
};

const TeenRequestsPanel = ({
  gigId,
  requiresFunding,
  selectsBeforePayment = false,
  requests,
  teenDetailsByRequestId = {},
  loadingTeenRequestIds = [],
  highlightedRequestId = null,
  approvingRequestId,
  rejectingRequestId,
  schedulingRequestId,
  onAcceptTeen,
  onRejectTeen,
  onViewTeen,
  onOpenConversation,
}: TeenRequestsPanelProps) => {
  const groupedRequests = useMemo(
    () => REQUEST_GROUPS
      .map((group) => ({
        ...group,
        requests: requests.filter((request) => group.statuses.includes(request.status)),
      }))
      .filter((group) => group.requests.length > 0),
    [requests],
  );

  return (
    <div className="min-w-0">
      {groupedRequests.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background p-6 text-sm text-myteengig-text-muted">
          No applicants are waiting on your decision right now. Parent or guardian approvals will appear here after the request reaches your review flow.
        </div>
      ) : (
        <div className="grid min-w-0 gap-5">
          {groupedRequests.map((group) => (
            <section key={group.key} className="min-w-0">
              <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
                <div>
                  <h2 className="text-sm font-semibold text-myteengig-text-primary">{group.title}</h2>
                  <p className="text-xs text-myteengig-text-muted">{group.description}</p>
                </div>
                <span className="rounded-full border border-myteengig-border bg-white px-2.5 py-1 text-xs font-semibold text-myteengig-text-primary">
                  {group.requests.length}
                </span>
              </div>
              <div className="grid min-w-0 gap-3">
                {group.requests.map((request) => (
                  <TeenRequestRow
                    key={request.requestId}
                    gigId={gigId}
                    request={request}
                    teenDetail={teenDetailsByRequestId[request.requestId]}
                    isTeenDetailLoading={loadingTeenRequestIds.includes(request.requestId)}
                    isHighlighted={highlightedRequestId === request.requestId}
                    requiresFunding={requiresFunding}
                    selectsBeforePayment={selectsBeforePayment}
                    isApproving={approvingRequestId === request.requestId}
                    isRejecting={rejectingRequestId === request.requestId}
                    isScheduling={schedulingRequestId === request.requestId}
                    onAcceptTeen={onAcceptTeen}
                    onRejectTeen={onRejectTeen}
                    onViewTeen={onViewTeen}
                    onOpenConversation={onOpenConversation}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeenRequestsPanel;
